// Socket.IO factory for the admin chat surface (issue #400, Sprint 1 of
// Chat Epic #393).
//
// Replaces the previous module-scope `io.connect(...)` in
// `Messenger/index.tsx` so that every connection carries the admin's JWT
// via the Socket.IO `auth.token` channel. The backend gateway rejects
// handshakes without a valid token (#103) — without this factory, every
// admin session would land in `connect_error`.

import * as io from 'socket.io-client';

import { config } from 'src/config';

/**
 * One-release feature flag (#414). When `true`, the admin Messenger
 * subscribes to BOTH the legacy `'message'` and the new
 * `'chat:message:new'` events so a backend that only emits one of them
 * still updates the UI.
 *
 * Sprint 6 / #6.4 will:
 *   1. Flip this to `false` once the backend stops emitting the legacy
 *      `'message'` event.
 *   2. Delete the flag and the legacy subscribe block entirely.
 *
 * Keeping it as a const (rather than a runtime env var) is intentional:
 * the cleanup is a code change anyway, and a const is cheaper at runtime
 * than reading a config value on every `socket.on(...)` setup.
 */
export const USE_LEGACY_CHAT_EVENT = true;

/**
 * Build a fresh Socket.IO client for the chat namespace, authenticated with
 * the supplied access token. The factory deliberately does not memoize;
 * the lifecycle (one socket per signed-in admin session, disposed on
 * logout / token refresh) is owned by [`ChatSocketProvider`].
 */
export function createChatSocket(token: string) {
  if (!token) {
    // Guard: the backend handshake rejects empty tokens with `connect_error`.
    // Surfacing the bad input here avoids spinning up a socket that we know
    // cannot complete the handshake.
    throw new Error('createChatSocket requires a non-empty JWT');
  }

  return io.connect(config.SOCKET_URL || config.FILe_URL, {
    transports: ['websocket'],
    auth: { token },
  });
}

export type ChatSocket = ReturnType<typeof createChatSocket>;
